import { GridPaginationModel } from '@mui/x-data-grid-premium'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { RequestStepStateEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { RequestPriorityEnum } from '@omnea-app/packages/universal/requests/requestPriority'
import { ValidUuidString, isUuid } from '@omnea-app/packages/universal/string-utils/isUuid'
import { BlockTypeEnum } from '@omnea-app/packages/universal/workflow/model/utils/blocks'
import { TaskStatusEnum } from '@omnea-app/requests/requestsFrontendClient'
import { TagWithIdAndName } from '@omnea-app/tags/tagsFrontendClient'
import { TasksTopLevelFilterKey } from '@omnea-app/tasks/tasksFrontendClient'
import { Task } from '@omnea-app/tasks/tasksHttpClient'
import { useTasks, useTasksPage } from '../useTasks'
import { ServerTaskQuery } from './toServerTaskQuery'

type RequestStepTask = Extract<Task, { type: 'REQUEST_STEP' }>
type GlobalTask = Extract<Task, { type: 'GLOBAL' }>
type ConfirmGoodsReceivedTask = Extract<GlobalTask, { subtype: 'CONFIRM_GOODS_RECEIVED' }>
type ApproveBillTask = Extract<GlobalTask, { subtype: 'APPROVE_BILL' }>

export type TaskRow = {
  id: string
  requestId: string | null
  billId: string | null
  globalSubtype: GlobalTask['subtype'] | null
  stageType: BlockTypeEnum | null
  name: string
  publicId: string
  department: string | undefined
  label: string
  supplier: { name?: string; logoUrl?: string | null } | null
  type: string
  dueDate: Date | null
  createdAt: string
  state: RequestStepStateEnum
  stage: number
  totalStages: number
  status: TaskStatusEnum
  isRejected: boolean
  activeAmendmentRequests: { id: string }[]
  buyerLegalEntity: RequestStepTask['request']['buyerLegalEntity']
  value: RequestStepTask['request']['value']
  owner: RequestStepTask['request']['participants']['projectOwner']
  requestor: RequestStepTask['request']['participants']['requestor'] | null
  tags: TagWithIdAndName[]
  autoGeneratedTags: TagWithIdAndName[]
  priority: RequestPriorityEnum | null
  requestStatus: RequestStepTask['request']['operationalStatus'] | null
  requestStatusDetail: RequestStepTask['request']['operationalStatusDetail'] | null
  currentUserAssignees: RequestStepTask['currentUserAssignees'] | null
  currentGroupAssignees: RequestStepTask['currentGroupAssignees'] | null
  allCurrentAssignees: string[]
  activeSubTaskIds: string[]
  activeSubTaskTypes: string[]
}

const buildConfirmGoodsReceivedRow = (task: ConfirmGoodsReceivedTask, label: string): TaskRow => ({
  id: task.id,
  requestId: task.request.id,
  billId: null,
  globalSubtype: task.subtype,
  name: task.request.name,
  publicId: task.request.publicId,
  department: task.request.departmentName,
  label,
  stageType: null,
  type: task.request.workflowTitle,
  supplier: { name: task.request.supplierName, logoUrl: task.request.supplierLogoUrl },
  dueDate: null,
  createdAt: task.createdAt,
  state: RequestStepStateEnum.RUNNING,
  stage: 0,
  totalStages: 0,
  status: TaskStatusEnum.IN_PROGRESS,
  isRejected: false,
  activeAmendmentRequests: [],
  buyerLegalEntity: task.request.buyerLegalEntity,
  value: task.request.value,
  owner: task.request.participants.projectOwner,
  requestor: task.request.participants.requestor,
  tags: task.request.tags,
  autoGeneratedTags: task.request.autoGeneratedTags,
  priority: task.request.priority as RequestPriorityEnum,
  requestStatus: null,
  requestStatusDetail: null,
  currentUserAssignees: task.currentUserAssignees,
  currentGroupAssignees: [],
  allCurrentAssignees: task.currentUserAssignees.map((u) => `${u.firstName} ${u.lastName}`),
  activeSubTaskIds: [],
  activeSubTaskTypes: [],
})

const buildApproveBillRow = (task: ApproveBillTask, label: string): TaskRow => ({
  id: task.id,
  requestId: null,
  billId: task.bill.id,
  globalSubtype: task.subtype,
  name: '',
  publicId: task.bill.invoiceNumber ?? '',
  department: undefined,
  label,
  stageType: null,
  type: '',
  supplier: { name: task.bill.supplierName ?? undefined, logoUrl: task.bill.supplierLogoUrl },
  dueDate: null,
  createdAt: task.createdAt,
  state: RequestStepStateEnum.RUNNING,
  stage: 0,
  totalStages: 0,
  status: TaskStatusEnum.IN_PROGRESS,
  isRejected: false,
  activeAmendmentRequests: [],
  buyerLegalEntity: null,
  value: null,
  owner: null,
  requestor: null,
  tags: [],
  autoGeneratedTags: [],
  priority: null,
  requestStatus: null,
  requestStatusDetail: null,
  currentUserAssignees: task.currentUserAssignees,
  currentGroupAssignees: [],
  allCurrentAssignees: task.currentUserAssignees.map((u) => `${u.firstName} ${u.lastName}`),
  activeSubTaskIds: [],
  activeSubTaskTypes: [],
})

type ServerPage = { paginationModel: GridPaginationModel; query: ServerTaskQuery }
type FullList = { isReady: boolean; hasFailed: boolean }

type RowsOptions = {
  // Fetch only this page of the view; `totalCount` is then the server's total. Without it, every task is fetched
  // for the grid to filter, sort and page itself.
  serverPage?: ServerPage | null
  // Load every task in the background while a server page is shown; `fullList` says when it landed or failed.
  prefetchFullList?: boolean
  // Fetch nothing yet (the view isn't known), reporting loading.
  isWaiting?: boolean
}

export const useTaskTableRowsForCurrentUser = (
  topLevelFilterKeyProp: TasksTopLevelFilterKey | ValidUuidString,
  { serverPage = null, prefetchFullList = false, isWaiting = false }: RowsOptions = {}
): { rows: TaskRow[]; loading: boolean; totalCount: number | undefined; fullList: FullList } => {
  const { t } = useTranslation('tasks')
  const topLevelFilterKey = useMemo(() => {
    if (isUuid(topLevelFilterKeyProp)) {
      return null
    }
    return topLevelFilterKeyProp
  }, [topLevelFilterKeyProp])

  const allTasks = useTasks({ topLevelFilterKey, enabled: !isWaiting && (serverPage === null || prefetchFullList) })
  // Both wait for any fetch in flight: an errored query stays isError while a retry runs, and a cached success
  // can be refetching.
  // A cached list from before the view was known isn't this view's list, so nothing is ready while waiting.
  const fullList = {
    isReady: !isWaiting && allTasks.isSuccess && !allTasks.isFetching,
    hasFailed: !isWaiting && allTasks.isError && !allTasks.isFetching,
  }
  const tasksPage = useTasksPage({
    topLevelFilterKey,
    paginationModel: serverPage?.paginationModel ?? { page: 0, pageSize: 0 },
    query: serverPage?.query ?? { filterModel: { items: [] }, sortModel: [] },
    enabled: !isWaiting && serverPage !== null,
  })
  const tasks = (serverPage ? tasksPage.data?.tasks : allTasks.data) ?? []
  // The previous page's rows stay visible under the loading state, so they can't be mistaken for the new page.
  const loading = isWaiting || (serverPage ? tasksPage.isLoading || tasksPage.isPlaceholderData : allTasks.isLoading)
  const totalCount = serverPage ? tasksPage.data?.totalCount : undefined

  // Rows from an older API deploy lack a `type` discriminator; treat anything not GLOBAL as a request step.
  // A GLOBAL subtype this build cannot render is dropped rather than mapped: the frontend deploys
  // ahead of the backend, so it always sees subtypes it has no row for yet.
  const rows = tasks.flatMap((task) => {
    if (task.type !== 'GLOBAL') return [buildRequestStepRow(task)]

    const label = t(`global-tasks.${task.subtype}`)

    switch (task.subtype) {
      case 'CONFIRM_GOODS_RECEIVED':
        return [buildConfirmGoodsReceivedRow(task, label)]
      case 'APPROVE_BILL':
        return [buildApproveBillRow(task, label)]
      default:
        return []
    }
  })

  return { rows, loading, totalCount, fullList }
}

const buildRequestStepRow = (task: RequestStepTask): TaskRow => ({
  id: task.id,
  requestId: task.request.id,
  billId: null,
  globalSubtype: null,
  stageType: task.stageType as BlockTypeEnum,
  state: task.state as RequestStepStateEnum,
  stage: task.stage,
  totalStages: task.totalStages,
  status: task.status as TaskStatusEnum,
  isRejected: task.isRejected,
  name: task.request.name,
  label: task.stepLabel,
  supplier: {
    name: task.request.supplierName,
    logoUrl: task.request.supplierLogoUrl,
  },
  publicId: task.request.publicId,
  department: task.request.departmentName,
  type: task.request.workflowTitle,
  dueDate: new Date(task.dueDate),
  createdAt: task.createdAt,
  activeAmendmentRequests: task.activeAmendments,
  tags: task.request.tags,
  autoGeneratedTags: task.request.autoGeneratedTags,
  priority: task.request.priority as RequestPriorityEnum,
  buyerLegalEntity: task.request.buyerLegalEntity,
  value: task.request.value,
  requestStatus: task.request.operationalStatus,
  requestStatusDetail: task.request.operationalStatusDetail,
  owner: task.request.participants.projectOwner,
  requestor: task.request.participants.requestor,
  currentUserAssignees: task.currentUserAssignees,
  currentGroupAssignees: task.currentGroupAssignees,
  allCurrentAssignees: [
    ...task.currentUserAssignees.map((u) => `${u.firstName} ${u.lastName}`),
    ...task.currentGroupAssignees.map((g) => g.name),
  ],
  activeSubTaskIds: (task.subTasks || []).filter((subTask) => !subTask.completedOn).map((subTask) => subTask.id),
  activeSubTaskTypes: (task.subTasks || []).filter((subTask) => !subTask.completedOn).map((subTask) => subTask.type),
})
